import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Tooltip,
  Collapse,
  Tabs,
  Tab,
  Alert,
  Autocomplete,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AddCircleOutline as AddCircleIcon,
  RemoveCircleOutline as RemoveCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  WifiOff as WifiOffIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  listCollectionSupervisors,
  listSupervisorMasters,
  createSupervisorMaster,
  updateSupervisorMaster,
  deleteSupervisorMaster,
  uploadSupervisorMasterExcel,
} from '../actions/supervisorMasterActions';
import { listEmployees } from '../actions/employeeMasterActions';
import { listActiveCompanies } from '../actions/companyActions';
import {
  SUPERVISOR_MASTER_CREATE_RESET,
  SUPERVISOR_MASTER_UPDATE_RESET,
  SUPERVISOR_MASTER_DELETE_RESET,
  SUPERVISOR_MASTER_UPLOAD_RESET,
} from '../constants/supervisorMasterConstants';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const now = new Date();
const CURRENT_MONTH = now.getMonth() + 1;
const CURRENT_YEAR = now.getFullYear();

const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

const emptyTeamMember = { name: '', identifier: '', assignments: [], advance: '' };
const emptyProduct = {
  productName: '', bktName: '', region: '', billingTarget: '', distributionPercent: '',
  gstNumber: '', cgstSgst: '', igst: '',
};

const isDelhiRegion = (region) => (region || '').trim().toLowerCase() === 'delhi';
const emptyBank = { bankName: '', products: [{ ...emptyProduct }] };

// Backend stores one flat entry per (team member, assignedBank, assignedProduct) combination.
// Group flat entries sharing the same identifier/name back into one UI row with multiple assignments.
const groupTeamMembers = (teamMembers) => {
  const rows = [];
  const rowByKey = new Map();
  (teamMembers || []).forEach((tm) => {
    const key = tm.identifier || tm.name;
    let row = rowByKey.get(key);
    if (!row) {
      row = {
        name: tm.name,
        identifier: tm.identifier || '',
        advance: tm.advance ?? '',
        assignments: [],
      };
      rowByKey.set(key, row);
      rows.push(row);
    }
    if (tm.assignedBank || tm.assignedProduct) {
      row.assignments.push({
        assignedBank: tm.assignedBank || '',
        assignedProduct: tm.assignedProduct || '',
      });
    }
  });
  return rows;
};

const emptyForm = {
  month: CURRENT_MONTH,
  year: CURRENT_YEAR,
  supervisorName: '',
  companyName: '',
  supervisorAdvance: '',
  type: 'collection',
  banks: [{ ...emptyBank, products: [{ ...emptyProduct }] }],
  teamMembers: [],
};

const SupervisorMaster = () => {
  const dispatch = useDispatch();
  const isOnline = useOnlineStatus();

  const [search, setSearch] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState(CURRENT_YEAR);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [expandedBanks, setExpandedBanks] = useState({});
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Upload state
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMonth, setUploadMonth] = useState(CURRENT_MONTH);
  const [uploadYear, setUploadYear] = useState(CURRENT_YEAR);
  const [uploadErrorDialog, setUploadErrorDialog] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);

  const { userLogin: { userInfo } } = useSelector((s) => s);
  const getRoleData = useSelector((s) => s.getRoleInfo?.getRoleData || {});

  const { loading: loadingSupervisors, supervisors = [] } = useSelector(
    (s) => s.supervisorUsers,
  );
  const { employees: activeEmployees = [] } = useSelector((s) => s.employeeList);
  const { companies = [] } = useSelector((s) => s.companyList);
  const { loading: loadingList, records = [], totalRecords = 0 } = useSelector(
    (s) => s.supervisorMasterList,
  );
  const supervisorMasterCreate = useSelector((s) => s.supervisorMasterCreate);
  const supervisorMasterUpdate = useSelector((s) => s.supervisorMasterUpdate);
  const supervisorMasterDelete = useSelector((s) => s.supervisorMasterDelete);
  const supervisorMasterUpload = useSelector((s) => s.supervisorMasterUpload);

  const fetchList = useCallback(() => {
    dispatch(listSupervisorMasters(search, page + 1, rowsPerPage, filterMonth, filterYear));
  }, [dispatch, search, page, rowsPerPage, filterMonth, filterYear]);

  useEffect(() => {
    dispatch(listCollectionSupervisors());
    dispatch(listActiveCompanies());
  }, [dispatch]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Create success
  useEffect(() => {
    if (supervisorMasterCreate.success) {
      toast.success('Supervisor master created successfully');
      dispatch({ type: SUPERVISOR_MASTER_CREATE_RESET });
      setOpenDialog(false);
      fetchList();
    }
    if (supervisorMasterCreate.error) {
      toast.error(supervisorMasterCreate.error);
      dispatch({ type: SUPERVISOR_MASTER_CREATE_RESET });
    }
  }, [supervisorMasterCreate, dispatch, fetchList]);

  // Update success
  useEffect(() => {
    if (supervisorMasterUpdate.success) {
      toast.success('Supervisor master updated successfully');
      dispatch({ type: SUPERVISOR_MASTER_UPDATE_RESET });
      setOpenDialog(false);
      fetchList();
    }
    if (supervisorMasterUpdate.error) {
      toast.error(supervisorMasterUpdate.error);
      dispatch({ type: SUPERVISOR_MASTER_UPDATE_RESET });
    }
  }, [supervisorMasterUpdate, dispatch, fetchList]);

  // Delete success
  useEffect(() => {
    if (supervisorMasterDelete.success) {
      toast.success('Supervisor master deleted successfully');
      dispatch({ type: SUPERVISOR_MASTER_DELETE_RESET });
      setDeleteDialogOpen(false);
      fetchList();
    }
    if (supervisorMasterDelete.error) {
      toast.error(supervisorMasterDelete.error);
      dispatch({ type: SUPERVISOR_MASTER_DELETE_RESET });
    }
  }, [supervisorMasterDelete, dispatch, fetchList]);

  // Upload success/fail
  useEffect(() => {
    if (supervisorMasterUpload.success) {
      const r = supervisorMasterUpload.uploadResult;
      toast.success(`Upload complete! Created: ${r.success}, Failed: ${r.failed}`);
      setUploadDialog(false);
      setUploadFile(null);
      fetchList();
      dispatch({ type: SUPERVISOR_MASTER_UPLOAD_RESET });
      if (r.errors && r.errors.length > 0) {
        setUploadErrors(r.errors);
        setUploadErrorDialog(true);
      }
    }
    if (supervisorMasterUpload.error) {
      toast.error(supervisorMasterUpload.error);
      dispatch({ type: SUPERVISOR_MASTER_UPLOAD_RESET });
    }
  }, [supervisorMasterUpload.success, supervisorMasterUpload.error]);

  const handleUpload = () => {
    if (!isOnline) return toast.error('No internet connection. Please check your network and try again.');
    if (!uploadFile) return toast.error('Please select an Excel file');
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('month', uploadMonth);
    fd.append('year', uploadYear);
    dispatch(uploadSupervisorMasterExcel(fd));
  };

  const handleCloseUploadDialog = () => {
    setUploadDialog(false);
    setUploadFile(null);
    dispatch({ type: SUPERVISOR_MASTER_UPLOAD_RESET });
  };

  const handleOpenCreate = () => {
    setEditMode(false);
    setEditId(null);
    setForm(emptyForm);
    setExpandedBanks({ 0: true });
    dispatch(listEmployees({ isLeft: false, limit: 5000 }));
    setOpenDialog(true);
  };

  const handleOpenEdit = (record) => {
    dispatch(listEmployees({ isLeft: false, limit: 5000 }));
    setEditMode(true);
    setEditId(record._id);
    setForm({
      month: record.month || CURRENT_MONTH,
      year: record.year || CURRENT_YEAR,
      supervisorName: record.supervisorName?._id || record.supervisorName || '',
      companyName: record.companyName || '',
      supervisorAdvance: record.supervisorAdvance ?? '',
      banks: record.banks?.length
        ? record.banks.map((b) => ({
            bankName: b.bankName,
            products: b.products?.length
              ? b.products.map((p) => ({
                  productName: p.productName,
                  bktName: p.bktName || '',
                  region: p.region || '',
                  billingTarget: p.billingTarget ?? '',
                  distributionPercent: p.distributionPercent,
                  gstNumber: p.gstNumber || '',
                  cgstSgst: p.cgstSgst ?? '',
                  igst: p.igst ?? '',
                }))
              : [{ ...emptyProduct }],
          }))
        : [{ ...emptyBank, products: [{ ...emptyProduct }] }],
      type: record.type || 'collection',
      teamMembers: groupTeamMembers(record.teamMembers),
    });
    const expanded = {};
    (record.banks || []).forEach((_, i) => { expanded[i] = true; });
    setExpandedBanks(expanded);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    dispatch({ type: SUPERVISOR_MASTER_CREATE_RESET });
    dispatch({ type: SUPERVISOR_MASTER_UPDATE_RESET });
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // ─── Banks ────────────────────────────────────────────────────────────────
  const addBank = () => {
    setForm((prev) => ({
      ...prev,
      banks: [...prev.banks, { bankName: '', products: [{ ...emptyProduct }] }],
    }));
    setExpandedBanks((prev) => ({ ...prev, [form.banks.length]: true }));
  };

  const removeBank = (bi) => {
    const updatedBanks = form.banks.filter((_, i) => i !== bi);
    // Remove assignments referencing the deleted bank
    const deletedBankName = form.banks[bi].bankName;
    const updatedTeam = form.teamMembers.map((tm) => ({
      ...tm,
      assignments: tm.assignments.filter((a) => a.assignedBank !== deletedBankName),
    }));
    setForm((prev) => ({ ...prev, banks: updatedBanks, teamMembers: updatedTeam }));
  };

  const updateBankName = (bi, value) => {
    const oldName = form.banks[bi].bankName;
    setForm((prev) => {
      const banks = prev.banks.map((b, i) =>
        i === bi ? { ...b, bankName: value } : b,
      );
      const teamMembers = prev.teamMembers.map((tm) => ({
        ...tm,
        assignments: tm.assignments.map((a) =>
          a.assignedBank === oldName ? { ...a, assignedBank: value } : a,
        ),
      }));
      return { ...prev, banks, teamMembers };
    });
  };

  const toggleBankExpand = (bi) => {
    setExpandedBanks((prev) => ({ ...prev, [bi]: !prev[bi] }));
  };

  // ─── Products ─────────────────────────────────────────────────────────────
  const addProduct = (bi) => {
    setForm((prev) => {
      const banks = prev.banks.map((b, i) =>
        i === bi
          ? { ...b, products: [...b.products, { ...emptyProduct }] }
          : b,
      );
      return { ...prev, banks };
    });
  };

  const removeProduct = (bi, pi) => {
    const deletedProductName = form.banks[bi].products[pi].productName;
    const bankName = form.banks[bi].bankName;
    setForm((prev) => {
      const banks = prev.banks.map((b, i) =>
        i === bi
          ? { ...b, products: b.products.filter((_, j) => j !== pi) }
          : b,
      );
      const teamMembers = prev.teamMembers.map((tm) => ({
        ...tm,
        assignments: tm.assignments.filter(
          (a) => !(a.assignedBank === bankName && a.assignedProduct === deletedProductName),
        ),
      }));
      return { ...prev, banks, teamMembers };
    });
  };

  const updateProduct = (bi, pi, field, value) => {
    const oldProductName = form.banks[bi].products[pi].productName;
    const bankName = form.banks[bi].bankName;
    setForm((prev) => {
      const banks = prev.banks.map((b, i) =>
        i === bi
          ? {
              ...b,
              products: b.products.map((p, j) =>
                j === pi ? { ...p, [field]: value } : p,
              ),
            }
          : b,
      );
      let teamMembers = prev.teamMembers;
      if (field === 'productName') {
        teamMembers = prev.teamMembers.map((tm) => ({
          ...tm,
          assignments: tm.assignments.map((a) =>
            a.assignedBank === bankName && a.assignedProduct === oldProductName
              ? { ...a, assignedProduct: value }
              : a,
          ),
        }));
      }
      return { ...prev, banks, teamMembers };
    });
  };

  // ─── Team Members ─────────────────────────────────────────────────────────
  const addTeamMember = () => {
    setForm((prev) => ({
      ...prev,
      teamMembers: [...prev.teamMembers, { ...emptyTeamMember, assignments: [] }],
    }));
  };

  const removeTeamMember = (ti) => {
    setForm((prev) => ({
      ...prev,
      teamMembers: prev.teamMembers.filter((_, i) => i !== ti),
    }));
  };

  const updateTeamMember = (ti, field, value) => {
    setForm((prev) => {
      const teamMembers = prev.teamMembers.map((tm, i) => {
        if (i !== ti) return tm;
        if (field === 'identifier') {
          const matched = activeEmployees.find(
            (e) => e.employeeCode?.toLowerCase() === value.toLowerCase(),
          );
          const fullName = matched
            ? [matched.firstName, matched.middleName, matched.lastName].filter(Boolean).join(' ')
            : '';
          return { ...tm, identifier: value, name: fullName };
        }
        return { ...tm, [field]: value };
      });
      return { ...prev, teamMembers };
    });
  };

  // Combined list of every valid (bank, product) pair currently defined in the Banks & Products section
  const getBankProductOptions = () =>
    form.banks.flatMap((b) =>
      b.bankName.trim()
        ? b.products
            .filter((p) => p.productName.trim())
            .map((p) => ({
              key: `${b.bankName}::${p.productName}`,
              bankName: b.bankName,
              productName: p.productName,
              label: `${b.bankName} - ${p.productName}`,
            }))
        : [],
    );

  const updateTeamMemberAssignments = (ti, selectedKeys) => {
    setForm((prev) => {
      const teamMembers = prev.teamMembers.map((tm, i) => {
        if (i !== ti) return tm;
        const assignments = selectedKeys.map((key) => {
          const [assignedBank, assignedProduct] = key.split('::');
          return { assignedBank, assignedProduct };
        });
        return { ...tm, assignments };
      });
      return { ...prev, teamMembers };
    });
  };

  const handleSubmit = () => {
    if (!form.supervisorName) return toast.error('Please select a supervisor');
    if (!form.companyName.trim()) return toast.error('Company name is required');
    const namedBanks = form.banks.filter((b) => b.bankName.trim());
    const hasUnregionedProduct = namedBanks.some((b) =>
      b.products.some((p) => p.productName.trim() && !p.region.trim()),
    );
    if (hasUnregionedProduct) {
      return toast.error('Region is required for each product');
    }

    const payload = {
      month: form.month,
      year: form.year,
      supervisorName: form.supervisorName,
      companyName: form.companyName.trim(),
      supervisorAdvance: form.supervisorAdvance !== '' ? Number(form.supervisorAdvance) : null,
      banks: namedBanks.map((b) => ({
        bankName: b.bankName.trim(),
        products: b.products
          .filter((p) => p.productName.trim())
          .map((p) => {
            const product = {
              productName: p.productName.trim(),
              bktName: p.bktName.trim(),
              region: p.region.trim(),
              billingTarget: p.billingTarget !== '' ? Number(p.billingTarget) : null,
              distributionPercent: Number(p.distributionPercent) || 0,
              gstNumber: (p.gstNumber || '').trim(),
              cgstSgst: null,
              igst: null,
            };
            if (form.type === 'verification') {
              if (isDelhiRegion(p.region)) {
                product.cgstSgst = p.cgstSgst !== '' ? Number(p.cgstSgst) : null;
              } else {
                product.igst = p.igst !== '' ? Number(p.igst) : null;
              }
            }
            return product;
          }),
      })),
      type: form.type || 'collection',
      // Backend stores one flat entry per (member, bank, product) combo, so expand
      // each team member's selected assignments into individual entries here.
      teamMembers: form.teamMembers
        .filter((tm) => tm.name.trim())
        .flatMap((tm) => {
          const base = {
            name: tm.name.trim(),
            identifier: tm.identifier.trim(),
            advance: tm.advance !== '' ? Number(tm.advance) : null,
          };
          if (!tm.assignments.length) {
            return [{ ...base, assignedBank: '', assignedProduct: '' }];
          }
          return tm.assignments.map((a) => ({
            ...base,
            assignedBank: a.assignedBank,
            assignedProduct: a.assignedProduct,
          }));
        }),
    };

    if (editMode) {
      dispatch(updateSupervisorMaster(editId, payload));
    } else {
      dispatch(createSupervisorMaster(payload));
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    dispatch(deleteSupervisorMaster(deleteId));
  };

  const isSaving =
    supervisorMasterCreate.loading || supervisorMasterUpdate.loading;

  return (
    <Box>
      {!isOnline && (
        <Alert severity="warning" icon={<WifiOffIcon />} sx={{ mb: 2 }}>
          Internet is not available. Some actions like importing Excel may fail until your connection is restored.
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          Supervisor's Master
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {getRoleData.supervisorMasterAdd === true && (
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setUploadDialog(true)}>
              Upload Excel
            </Button>
          )}
          {getRoleData.supervisorMasterAdd === true && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
              Add New
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Month</InputLabel>
              <Select
                value={filterMonth}
                label="Month"
                onChange={(e) => { setFilterMonth(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Months</MenuItem>
                {MONTH_NAMES.map((m, i) => (
                  <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select
                value={filterYear}
                label="Year"
                onChange={(e) => { setFilterYear(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Years</MenuItem>
                {YEAR_OPTIONS.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={5} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by company or region..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchList}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        {loadingList ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ width: 40, p: 0 }} />
                    <TableCell><strong>#</strong></TableCell>
                    <TableCell><strong>Month</strong></TableCell>
                    <TableCell><strong>Supervisor Name</strong></TableCell>
                    <TableCell><strong>Identifier</strong></TableCell>
                    <TableCell><strong>Company</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Team Members</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    records.map((record, idx) => {
                      const isExpanded = expandedRows.has(record._id);
                      return (
                        <React.Fragment key={record._id}>
                          {/* Main row */}
                          <TableRow hover>
                            <TableCell sx={{ width: 40, p: 0, pl: 0.5 }}>
                              <Tooltip title={isExpanded ? 'Hide Banks & Products' : 'Show Banks & Products'}>
                                <IconButton size="small" onClick={() => toggleRow(record._id)}>
                                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                            <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                            <TableCell>
                              <Chip
                                label={`${MONTH_NAMES[(record.month || 1) - 1]} ${record.year}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>{record.supervisorName?.name || '—'}</TableCell>
                            <TableCell>{record.supervisorName?.identifier || '—'}</TableCell>
                            <TableCell>{record.companyName}</TableCell>
                            <TableCell>
                              <Chip
                                label={record.type === 'verification' ? 'Verification' : 'Collection'}
                                size="small"
                                color={record.type === 'verification' ? 'secondary' : 'primary'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {new Set((record.teamMembers || []).map((tm) => tm.identifier || tm.name)).size}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {getRoleData.supervisorMasterUpdate === true && (
                                  <Tooltip title="Edit">
                                    <IconButton size="small" color="primary" onClick={() => handleOpenEdit(record)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {getRoleData.supervisorMasterDelete === true && (
                                  <Tooltip title="Delete">
                                    <IconButton size="small" color="error" onClick={() => handleDeleteClick(record._id)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* Expandable banks & products sub-row */}
                          <TableRow>
                            <TableCell colSpan={9} sx={{ py: 0, border: isExpanded ? undefined : 0 }}>
                              <Collapse in={isExpanded} unmountOnExit>
                                <Box sx={{ px: 3, py: 2, backgroundColor: '#f9fafb', borderTop: '1px solid #e0e0e0' }}>
                                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: 'text.secondary' }}>
                                    Banks & Products
                                  </Typography>
                                  <Grid container spacing={2}>
                                    {(record.banks || []).map((b, bi) => (
                                      <Grid item key={bi} xs={12} sm={6} md={4}>
                                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                                          <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                                            {b.bankName}
                                          </Typography>
                                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                            {(b.products || []).map((p, pi) => (
                                              <Box key={pi} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                <Chip
                                                  label={`${p.productName}${p.distributionPercent ? ` · ${p.distributionPercent}%` : ''}`}
                                                  size="small"
                                                  variant="outlined"
                                                  color="primary"
                                                />
                                                {p.bktName && (
                                                  <Chip label={p.bktName} size="small" variant="outlined" />
                                                )}
                                                {p.region && (
                                                  <Chip label={p.region} size="small" variant="outlined" />
                                                )}
                                                {p.billingTarget != null && (
                                                  <Chip label={`Target: ${p.billingTarget}`} size="small" variant="outlined" color="secondary" />
                                                )}
                                                {p.gstNumber && (
                                                  <Chip label={`GST: ${p.gstNumber}`} size="small" variant="outlined" />
                                                )}
                                                {record.type === 'verification' && p.cgstSgst != null && (
                                                  <Chip label={`CGST/SGST: ${p.cgstSgst}%`} size="small" variant="outlined" color="info" />
                                                )}
                                                {record.type === 'verification' && p.igst != null && (
                                                  <Chip label={`IGST: ${p.igst}%`} size="small" variant="outlined" color="info" />
                                                )}
                                              </Box>
                                            ))}
                                          </Box>
                                        </Paper>
                                      </Grid>
                                    ))}
                                    {(record.banks || []).length === 0 && (
                                      <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">No banks assigned</Typography>
                                      </Grid>
                                    )}
                                  </Grid>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </Paper>

      {/* Create / Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Supervisor Master' : 'Add Supervisor Master'}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
            {MONTH_NAMES[form.month - 1]} {form.year}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Month & Year */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Month</InputLabel>
                <Select
                  value={form.month}
                  label="Month"
                  onChange={(e) => handleFieldChange('month', e.target.value)}
                >
                  {MONTH_NAMES.map((m, i) => {
                    const monthNum = i + 1;
                    return <MenuItem key={monthNum} value={monthNum}>{m}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Year</InputLabel>
                <Select
                  value={form.year}
                  label="Year"
                  onChange={(e) => handleFieldChange('year', e.target.value)}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Supervisor Name */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Supervisor Name</InputLabel>
                <Select
                  value={form.supervisorName}
                  label="Supervisor Name"
                  onChange={(e) => handleFieldChange('supervisorName', e.target.value)}
                >
                  {loadingSupervisors ? (
                    <MenuItem disabled>Loading...</MenuItem>
                  ) : supervisors.length === 0 ? (
                    <MenuItem disabled>No Collection Supervisors found</MenuItem>
                  ) : (
                    supervisors.map((s) => (
                      <MenuItem key={s._id} value={s._id}>
                        {s.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Company Name */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                fullWidth
                size="small"
                options={companies.map((c) => c.companyName)}
                value={form.companyName}
                onChange={(_, value) => handleFieldChange('companyName', value || '')}
                onInputChange={(_, value) => handleFieldChange('companyName', value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company Name"
                    required
                  />
                )}
              />
            </Grid>

            {/* Supervisor Advance (optional) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Supervisor Advance"
                type="number"
                value={form.supervisorAdvance}
                onChange={(e) => handleFieldChange('supervisorAdvance', e.target.value)}
                helperText="Optional"
              />
            </Grid>

            {/* Supervisor Type Tabs */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Tabs
                value={form.type}
                onChange={(_, v) => handleFieldChange('type', v)}
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="Collection" value="collection" />
                <Tab label="Verification" value="verification" />
              </Tabs>
            </Grid>

            {/* Banks & Products Section */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Banks & Products
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddCircleIcon />}
                  onClick={addBank}
                  variant="outlined"
                >
                  Add Bank
                </Button>
              </Box>

              {form.banks.map((bank, bi) => (
                <Paper
                  key={bi}
                  variant="outlined"
                  sx={{ p: 1.5, mb: 1.5, backgroundColor: '#fafafa' }}
                >
                  {/* Bank Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <IconButton size="small" onClick={() => toggleBankExpand(bi)}>
                      {expandedBanks[bi] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <TextField
                      size="small"
                      label={`Bank ${bi + 1} Name`}
                      value={bank.bankName}
                      onChange={(e) => updateBankName(bi, e.target.value)}
                      sx={{ flex: 1 }}
                    />
                    <Tooltip title="Remove Bank">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeBank(bi)}
                        disabled={form.banks.length === 1}
                      >
                        <RemoveCircleIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Products */}
                  <Collapse in={!!expandedBanks[bi]}>
                    <Box sx={{ pl: 4 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Products
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<AddCircleIcon />}
                          onClick={() => addProduct(bi)}
                        >
                          Add Product
                        </Button>
                      </Box>

                      {bank.products.map((product, pi) => (
                        <Paper key={pi} variant="outlined" sx={{ p: 1, mb: 1 }}>
                          <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12} sm={form.type === 'collection' ? 3 : 4}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Product Name"
                                value={product.productName}
                                onChange={(e) => updateProduct(bi, pi, 'productName', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={6} sm={2}>
                              <TextField
                                fullWidth
                                size="small"
                                label="BKT Name"
                                value={product.bktName}
                                onChange={(e) => updateProduct(bi, pi, 'bktName', e.target.value)}
                              />
                            </Grid>
                            {form.type === 'collection' && (
                              <Grid item xs={6} sm={2}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Distribution %"
                                  type="number"
                                  inputProps={{ min: 0, max: 100 }}
                                  value={product.distributionPercent}
                                  onChange={(e) => updateProduct(bi, pi, 'distributionPercent', e.target.value)}
                                />
                              </Grid>
                            )}
                            <Grid item xs={6} sm={2}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Region"
                                required
                                value={product.region}
                                onChange={(e) => updateProduct(bi, pi, 'region', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={6} sm={2}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Billing Target"
                                type="number"
                                value={product.billingTarget}
                                onChange={(e) => updateProduct(bi, pi, 'billingTarget', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12} sm={1}>
                              <Tooltip title="Remove Product">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => removeProduct(bi, pi)}
                                  disabled={bank.products.length === 1}
                                >
                                  <RemoveCircleIcon />
                                </IconButton>
                              </Tooltip>
                            </Grid>
                          </Grid>

                          {/* GST Number (all types) + CGST/SGST or IGST (verification only) */}
                          <Grid container spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                            <Grid item xs={12} sm={form.type === 'verification' ? 4 : 6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="GST Number"
                                value={product.gstNumber}
                                onChange={(e) => updateProduct(bi, pi, 'gstNumber', e.target.value)}
                              />
                            </Grid>
                            {form.type === 'verification' && (
                              isDelhiRegion(product.region) ? (
                                <Grid item xs={12} sm={4}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="CGST/SGST %"
                                    type="number"
                                    value={product.cgstSgst}
                                    onChange={(e) => updateProduct(bi, pi, 'cgstSgst', e.target.value)}
                                  />
                                </Grid>
                              ) : (
                                <Grid item xs={12} sm={4}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="IGST %"
                                    type="number"
                                    value={product.igst}
                                    onChange={(e) => updateProduct(bi, pi, 'igst', e.target.value)}
                                  />
                                </Grid>
                              )
                            )}
                          </Grid>
                        </Paper>
                      ))}
                    </Box>
                  </Collapse>
                </Paper>
              ))}
            </Grid>

            {/* Team Members Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Team Members
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddCircleIcon />}
                  onClick={addTeamMember}
                  variant="outlined"
                >
                  Add Member
                </Button>
              </Box>

              {form.teamMembers.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  No team members added yet. Click "Add Member" to add one.
                </Typography>
              )}

              {form.teamMembers.map((tm, ti) => (
                <Grid container spacing={1} key={ti} sx={{ mb: 1 }} alignItems="center">
                  <Grid item xs={form.type === 'verification' ? 6 : 2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Identifier"
                      value={tm.identifier}
                      onChange={(e) => updateTeamMember(ti, 'identifier', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={form.type === 'verification' ? 5 : 3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Member Name"
                      value={tm.name}
                      InputProps={{ readOnly: true }}
                      helperText={tm.identifier && !tm.name ? 'No active employee found' : ''}
                      error={Boolean(tm.identifier && !tm.name)}
                    />
                  </Grid>
                  {form.type === 'collection' && (
                    <Grid item xs={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Advance"
                        type="number"
                        value={tm.advance}
                        onChange={(e) => updateTeamMember(ti, 'advance', e.target.value)}
                      />
                    </Grid>
                  )}
                  {form.type === 'collection' && (
                    <Grid item xs={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Assigned Bank & Product</InputLabel>
                        <Select
                          multiple
                          value={tm.assignments.map((a) => `${a.assignedBank}::${a.assignedProduct}`)}
                          label="Assigned Bank & Product"
                          onChange={(e) => updateTeamMemberAssignments(ti, e.target.value)}
                          renderValue={(selected) =>
                            selected.map((key) => key.replace('::', ' - ')).join(', ')
                          }
                        >
                          {getBankProductOptions().length === 0 ? (
                            <MenuItem disabled><em>Add a bank & product first</em></MenuItem>
                          ) : (
                            getBankProductOptions().map((opt) => (
                              <MenuItem key={opt.key} value={opt.key}>
                                <Checkbox
                                  checked={tm.assignments.some(
                                    (a) => `${a.assignedBank}::${a.assignedProduct}` === opt.key,
                                  )}
                                />
                                <ListItemText primary={opt.label} />
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  <Grid item xs={1}>
                    <Tooltip title="Remove Member">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeTeamMember(ti)}
                      >
                        <RemoveCircleIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : null}
          >
            {isSaving ? 'Saving...' : editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this supervisor master record? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={supervisorMasterDelete.loading}
            startIcon={supervisorMasterDelete.loading ? <CircularProgress size={16} /> : null}
          >
            {supervisorMasterDelete.loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Excel Dialog */}
      <Dialog open={uploadDialog} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Supervisor Master Excel</DialogTitle>
        <DialogContent>
          {!isOnline && (
            <Alert severity="warning" icon={<WifiOffIcon />} sx={{ mb: 2 }}>
              Internet is not available. Please reconnect before uploading.
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Expected columns: IDENTIFIER, BANK, PRODUCT, COMPANY NAME, REGION, BILLING TARGET, TEAM MEMBER IDENTIFIER, ADVANCE
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            IDENTIFIER is matched to a Collection Supervisor, and TEAM MEMBER IDENTIFIER is matched to an active employee — both by their employee/user code. REGION and BILLING TARGET apply to the BANK/PRODUCT combination on that row.
          </Typography>
          <Button
            variant="text"
            size="small"
            startIcon={<DownloadIcon />}
            href="/supervisor_master_template.xlsx"
            download="supervisor_master_template.xlsx"
            sx={{ mb: 2 }}
          >
            Download Template
          </Button>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Month *</InputLabel>
                <Select
                  value={uploadMonth}
                  label="Month *"
                  onChange={(e) => setUploadMonth(e.target.value)}
                >
                  {MONTH_NAMES.map((m, i) => (
                    <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Year *</InputLabel>
                <Select
                  value={uploadYear}
                  label="Year *"
                  onChange={(e) => setUploadYear(e.target.value)}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                fullWidth
              >
                {uploadFile ? uploadFile.name : 'Choose Excel File (.xlsx / .xls / .csv)'}
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                />
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={supervisorMasterUpload.loading || !isOnline}
            startIcon={supervisorMasterUpload.loading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {supervisorMasterUpload.loading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Error Report Dialog */}
      <Dialog
        open={uploadErrorDialog}
        onClose={() => { setUploadErrorDialog(false); setUploadErrors([]); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Upload Errors ({uploadErrors.length} row{uploadErrors.length !== 1 ? 's' : ''} failed)
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The following rows could not be imported. Please fix the identifiers in your Excel file and re-upload, or add the records manually.
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {uploadErrors.map((err, i) => (
              <Typography
                key={i}
                component="li"
                variant="body2"
                sx={{ mb: 0.5, color: 'error.dark' }}
              >
                {err}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => { setUploadErrorDialog(false); setUploadErrors([]); }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupervisorMaster;
